import { _isEmpty, _isNil, _isNotBlank } from '@/littledash';
import { ENTITY_LABELS } from '../EntityTabs';
import type { SyncConfigSnapshot, SyncConfigurationAuditList, SyncEntityType } from '../SyncConfiguration.model';

type EntitySettings = SyncConfigSnapshot['entity_settings'][number];
type FieldMapping = EntitySettings['field_mappings'][number];
type AuditEntry = SyncConfigurationAuditList['entries'][number];

/** One attribute that changed between two snapshots; values are ids as recorded, null when absent. */
export interface AuditChange {
  /** The entity type's label, `Search schemas`, or null for team-wide settings. */
  entity: string | null;
  attribute: string;
  oldValue: string | null;
  newValue: string | null;
}

/** One row of the audit table: a single change, stamped with the audit entry it belongs to. */
export interface AuditChangeRow extends AuditChange {
  id: string;
  createdAt: string;
  actorName: string;
  action: string;
}

export const AUDIT_ACTION_LABELS: Record<AuditEntry['action'], string> = {
  CONFIG_UPDATED: 'Configuration updated',
  ENABLED: 'Switched to In Vivo configuration',
  DISABLED: 'Switched to Benchling Core configuration',
  IMPORT_COMMITTED: 'Configuration imported',
};

const SEARCH_SCHEMAS = 'Search schemas';

const entityLabel = (entityType: SyncEntityType): string => ENTITY_LABELS[entityType] ?? entityType;

const valueOrNull = (value: string | null | undefined): string | null => (_isNotBlank(value) ? value : null);

const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');

const activeConfiguration = (enabled: boolean): string => (enabled ? 'In Vivo' : 'Benchling Core');

const metadataTargetsByGlossary = (mappings: EntitySettings['metadata_mappings']): Map<string, string> => {
  const fieldsByGlossary = mappings.reduce<Map<string, Array<string>>>((acc, mapping) => {
    acc.set(mapping.metadata_glossary_api_id, [
      ...(acc.get(mapping.metadata_glossary_api_id) ?? []),
      mapping.benchling_field,
    ]);
    return acc;
  }, new Map());
  return new Map([...fieldsByGlossary].map(([glossaryId, fields]) => [glossaryId, [...fields].sort().join(', ')]));
};

/** Lists how a single entity's schema and mappings changed between two snapshots. */
const describeEntityChanges = (
  entityType: SyncEntityType,
  before: EntitySettings | undefined,
  after: EntitySettings | undefined
): Array<AuditChange> => {
  const entity = entityLabel(entityType);
  const change = (attribute: string, oldValue: string | null, newValue: string | null): AuditChange => ({
    entity,
    attribute,
    oldValue,
    newValue,
  });

  if (_isNil(after)) {
    return _isNil(before) ? [] : [change('Configuration', 'Configured', 'Removed')];
  }

  // A newly-configured entity diffs against an empty baseline, so the mappings it was created with
  // are listed too.
  const baseline: EntitySettings = before ?? {
    entity_type: entityType,
    schema_id: null,
    api_id_field: null,
    naming_strategy: null,
    treatment_schema_ids: null,
    metadata_mappings: [],
    field_mappings: [],
  };

  const changes: Array<AuditChange> = [];

  if (_isNil(before) || baseline.schema_id !== after.schema_id) {
    changes.push(change('Schema', valueOrNull(baseline.schema_id), valueOrNull(after.schema_id)));
  }
  if (baseline.naming_strategy !== after.naming_strategy) {
    changes.push(change('Naming strategy', valueOrNull(baseline.naming_strategy), valueOrNull(after.naming_strategy)));
  }

  // Compare the whole mapping record, not just benchling_field: custom_name/enabled/required
  // round-trip through the config, so a change to any of them is a real edit the audit must report.
  const beforeFields = new Map<string, FieldMapping>(
    baseline.field_mappings.map((mapping) => [mapping.source_field, mapping])
  );
  const afterFields = new Map<string, FieldMapping>(
    after.field_mappings.map((mapping) => [mapping.source_field, mapping])
  );
  afterFields.forEach((mapping, sourceField) => {
    const previous = beforeFields.get(sourceField);
    const attribute = `Field mapping: ${sourceField}`;
    if (_isNil(previous)) {
      changes.push(change(attribute, null, mapping.benchling_field));
      return;
    }
    if (previous.benchling_field !== mapping.benchling_field) {
      changes.push(change(attribute, previous.benchling_field, mapping.benchling_field));
    }
    if (previous.custom_name !== mapping.custom_name) {
      changes.push(
        change(`${attribute} custom name`, valueOrNull(previous.custom_name), valueOrNull(mapping.custom_name))
      );
    }
    if (previous.enabled !== mapping.enabled) {
      changes.push(change(`${attribute} enabled`, yesNo(previous.enabled), yesNo(mapping.enabled)));
    }
    if (previous.required !== mapping.required) {
      changes.push(change(`${attribute} required`, yesNo(previous.required), yesNo(mapping.required)));
    }
  });
  beforeFields.forEach((mapping, sourceField) => {
    if (!afterFields.has(sourceField)) {
      changes.push(change(`Field mapping: ${sourceField}`, mapping.benchling_field, null));
    }
  });

  // A metadata field may feed several Benchling fields, so each glossary's targets are compared as a
  // sorted, comma-joined list.
  const beforeMeta = metadataTargetsByGlossary(baseline.metadata_mappings);
  const afterMeta = metadataTargetsByGlossary(after.metadata_mappings);
  afterMeta.forEach((benchlingFields, glossaryId) => {
    const previous = beforeMeta.get(glossaryId) ?? null;
    if (previous !== benchlingFields) {
      changes.push(change(`Metadata mapping: ${glossaryId}`, previous, benchlingFields));
    }
  });
  beforeMeta.forEach((benchlingFields, glossaryId) => {
    if (!afterMeta.has(glossaryId)) {
      changes.push(change(`Metadata mapping: ${glossaryId}`, benchlingFields, null));
    }
  });

  return changes;
};

/** Diffs the treatment/disease search-schema assignments, keyed by schema id. */
const describeSearchSchemaChanges = (
  before: SyncConfigSnapshot['search_schemas'],
  after: SyncConfigSnapshot['search_schemas']
): Array<AuditChange> => {
  const rolesValue = (roles: Array<string> | undefined): string | null =>
    _isNil(roles) || _isEmpty(roles) ? null : roles.join(', ');
  const beforeBySchema = new Map(before.map((entry) => [entry.schema_id, entry.roles]));
  const afterBySchema = new Map(after.map((entry) => [entry.schema_id, entry.roles]));

  const changes: Array<AuditChange> = [];
  afterBySchema.forEach((roles, schemaId) => {
    const previous = beforeBySchema.get(schemaId);
    if (_isNil(previous) || previous.join(',') !== roles.join(',')) {
      changes.push({
        entity: SEARCH_SCHEMAS,
        attribute: `Schema ${schemaId}`,
        oldValue: rolesValue(previous),
        newValue: rolesValue(roles),
      });
    }
  });
  beforeBySchema.forEach((roles, schemaId) => {
    if (!afterBySchema.has(schemaId)) {
      changes.push({
        entity: SEARCH_SCHEMAS,
        attribute: `Schema ${schemaId}`,
        oldValue: rolesValue(roles),
        newValue: null,
      });
    }
  });
  return changes;
};

/**
 * What changed between two sync-configuration audit snapshots, one entry per attribute (ids shown
 * as-is). Returns an empty list when there is nothing to show (e.g. legacy entries).
 */
export const describeAuditChanges = (
  before: SyncConfigSnapshot | null,
  after: SyncConfigSnapshot | null
): Array<AuditChange> => {
  const changes: Array<AuditChange> = [];

  const wasEnabled = before?.enabled ?? false;
  const isEnabled = after?.enabled ?? false;
  if (wasEnabled !== isEnabled) {
    changes.push({
      entity: null,
      attribute: 'Active configuration',
      oldValue: activeConfiguration(wasEnabled),
      newValue: activeConfiguration(isEnabled),
    });
  }

  const oldRegistry = valueOrNull(before?.registry_id);
  const newRegistry = valueOrNull(after?.registry_id);
  if (oldRegistry !== newRegistry) {
    changes.push({ entity: null, attribute: 'Registry ID', oldValue: oldRegistry, newValue: newRegistry });
  }

  const beforeByEntity = new Map<SyncEntityType, EntitySettings>(
    (before?.entity_settings ?? []).map((settings) => [settings.entity_type, settings])
  );
  const afterByEntity = new Map<SyncEntityType, EntitySettings>(
    (after?.entity_settings ?? []).map((settings) => [settings.entity_type, settings])
  );
  const entityTypes = new Set<SyncEntityType>([...beforeByEntity.keys(), ...afterByEntity.keys()]);

  entityTypes.forEach((entityType) => {
    changes.push(...describeEntityChanges(entityType, beforeByEntity.get(entityType), afterByEntity.get(entityType)));
  });

  changes.push(...describeSearchSchemaChanges(before?.search_schemas ?? [], after?.search_schemas ?? []));

  return changes;
};

/**
 * Flattens audit entries into one table row per changed attribute, keeping the entries' order. An
 * entry with no recorded diff (e.g. a legacy entry) still gets a single row so the event is visible.
 */
export const toAuditChangeRows = (entries: Array<AuditEntry>): Array<AuditChangeRow> =>
  entries.reduce<Array<AuditChangeRow>>((rows, entry) => {
    const stamp = {
      createdAt: entry.created_at,
      actorName: entry.actor?.name ?? 'System',
      action: AUDIT_ACTION_LABELS[entry.action] ?? entry.action,
    };
    const changes = describeAuditChanges(entry.before_state, entry.after_state);
    if (_isEmpty(changes)) {
      rows.push({ id: entry.api_id, ...stamp, entity: null, attribute: '', oldValue: null, newValue: null });
      return rows;
    }
    changes.forEach((change, index) => {
      rows.push({ id: `${entry.api_id}-${index}`, ...stamp, ...change });
    });
    return rows;
  }, []);
